/**
 * The resumable upload of a file to the cloud session that vn opened (`uploadUrl`): one `PUT` of the whole file; after a network error ask the cloud how much arrived
 * (`PUT` with `Content-Range: bytes * / total`, an answer 308 with `Range: bytes=0-N`) and send the rest. The transport is a small interface, so the algorithm
 * is tested without a browser; `media-xhr-transport.ts` is the one that uses `XMLHttpRequest`.
 *
 * What the cloud documents as «ask how much arrived and go on» is treated the same as a lost connection: a request that got no answer (also one that stalled: the transport
 * gives up on it), and the answers 408, 429 and 5xx. A spell of such trouble is lived through for a time (`RECOVERY_BUDGET_MS`, waiting 1, 2, 4 … up to 30 s between the
 * questions), and the spell is over as soon as the cloud moves again (a data request is answered, or the question shows more bytes than before).
 *
 * When the browser cannot read `Range` of a 308 (the bucket's CORS does not expose it) the answer reads as «nothing arrived»: the file is sent again from byte 0, the progress goes back
 * to 0 (visibly), and the number of errors in a row and of 308s that move nothing is bounded, so it cannot go on for ever.
 *
 * Nothing here logs, stores or builds a message from the address of the session: it is a secret of the editor (§4 of the brief).
 */

export interface PutRequest {
  url: string;
  headers: Record<string, string>;
  body: Blob | null;
  /** The bytes of THIS request's body that went out. */
  onProgress?: (loaded: number) => void;
  signal?: AbortSignal;
}

export interface UploadResponse {
  status: number;
  header(name: string): string | null;
}

export interface UploadTransport {
  put(request: PutRequest): Promise<UploadResponse>;
}

/** The request did not get an answer (offline, reset, timeout): the upload goes on from where the cloud says it has. */
export class UploadNetworkError extends Error {
  constructor() {
    super('upload network error');
    this.name = 'UploadNetworkError';
  }
}

/** The editor (or the page) stopped the upload. */
export class UploadAbortedError extends Error {
  constructor() {
    super('upload aborted');
    this.name = 'UploadAbortedError';
  }
}

/** The address of the upload session is not one of the cloud's: nothing was sent to it (and nothing will be). The address itself is never in the message. */
export class UploadUrlRejectedError extends Error {
  constructor() {
    super('upload address refused');
    this.name = 'UploadUrlRejectedError';
  }
}

/** The cloud answered with a status that is neither "done" nor "send more" (the session is gone, the object is refused). */
export class UploadHttpError extends Error {
  constructor(readonly status: number) {
    super(`upload refused: ${status}`);
    this.name = 'UploadHttpError';
  }
}

/** `Range: bytes=0-262143` → 262144, the offset to go on from; no header = nothing has arrived = 0. */
export function nextOffsetFromRange(range: string | null | undefined): number {
  const match = /^bytes=\d+-(\d+)$/.exec((range ?? '').trim());
  return match ? Number(match[1]) + 1 : 0;
}

/** `Content-Range` of the rest of a file that is `total` bytes long, from `start`. */
export function contentRangeOf(start: number, total: number): string {
  return `bytes ${start}-${total - 1}/${total}`;
}

/** `Content-Range` of the question "how much has arrived?". */
export function contentRangeQuery(total: number): string {
  return `bytes */${total}`;
}

export const BACKOFF_CAP_MS = 30_000;

/** The wait before the n-th try after a failure (n from 1): 1 s, 2 s, 4 s … up to 30 s. */
export function backoffMs(attempt: number): number {
  return Math.min(BACKOFF_CAP_MS, 1000 * 2 ** Math.max(0, attempt - 1));
}

/** How long a spell of trouble (no answer, 408, 429, 5xx) is lived through before the upload gives up: ten minutes from its first failure. */
export const RECOVERY_BUDGET_MS = 10 * 60_000;

/** The answers of the cloud that mean «not now, ask again» (not «no»): the request timed out, too many requests, the cloud is unwell. */
export function isTransientStatus(status: number): boolean {
  return status === 408 || status === 429 || (status >= 500 && status <= 599);
}

export interface ResumableOptions {
  url: string;
  file: Blob;
  /** The sniffed type: the one the session was opened with. */
  contentType: string;
  signal?: AbortSignal;
  /** The bytes of the whole file that have gone out (offset + the part of this request). */
  onProgress?: (sent: number, total: number) => void;
  /** How long a spell of trouble is lived through, from its first failure until the cloud moves again (default `RECOVERY_BUDGET_MS`). */
  recoveryBudgetMs?: number;
  /** The network failed (or the cloud said «not now»): the upload is about to wait and ask the cloud where it is (`attempt` from 1). */
  onNetworkError?: (attempt: number) => void;
  /** The cloud answered the question again after the trouble: the connection is back (the upload goes on from where the cloud says it has). */
  onNetworkRestored?: () => void;
  /** A pause before the next try; the default is a timer that the abort cuts short. */
  wait?: (ms: number, signal?: AbortSignal) => Promise<void>;
  /** The clock in milliseconds; a test hands in its own. */
  now?: () => number;
}

export function waitMs(ms: number, signal?: AbortSignal): Promise<void> {
  return new Promise((resolve, reject) => {
    if (signal?.aborted) {
      reject(new UploadAbortedError());
      return;
    }
    const timer = setTimeout(() => {
      signal?.removeEventListener('abort', onAbort);
      resolve();
    }, ms);
    const onAbort = (): void => {
      clearTimeout(timer);
      reject(new UploadAbortedError());
    };
    signal?.addEventListener('abort', onAbort, { once: true });
  });
}

const DONE = [200, 201];
const RESUME = 308;
const MAX_STALLS = 3;

export async function uploadResumable(transport: UploadTransport, options: ResumableOptions): Promise<void> {
  const { url, file, contentType, signal } = options;
  const total = file.size;
  const budget = options.recoveryBudgetMs ?? RECOVERY_BUDGET_MS;
  const wait = options.wait ?? waitMs;
  const now = options.now ?? Date.now;
  const progress = (sent: number): void => options.onProgress?.(Math.min(total, sent), total);
  let offset = 0;
  // the spell of trouble the upload is in: how many failures, and since when (null = none)
  let errors = 0;
  let troubleSince: number | null = null;
  // a 308 to a request of the data that moved nothing: the cloud keeps asking for the same bytes (or its `Range` is not readable and it is taken for «nothing arrived»)
  let stalls = 0;
  const calm = (): void => {
    errors = 0;
    troubleSince = null;
  };

  /** After a failure: wait, then ask the cloud where it is. `total` = it has everything. Asks again, waiting longer, while the cloud itself is unwell, until the budget is spent. */
  const recover = async (): Promise<number> => {
    for (;;) {
      troubleSince ??= now();
      errors++;
      if (now() - troubleSince > budget) {
        throw new UploadNetworkError();
      }
      options.onNetworkError?.(errors);
      await wait(backoffMs(errors), signal);
      try {
        const res = await transport.put({ url, headers: { 'Content-Range': contentRangeQuery(total) }, body: null, signal });
        if (isTransientStatus(res.status)) {
          continue;
        }
        options.onNetworkRestored?.();
        if (DONE.includes(res.status)) {
          return total;
        }
        if (res.status === RESUME) {
          return nextOffsetFromRange(res.header('Range'));
        }
        throw new UploadHttpError(res.status);
      } catch (e) {
        if (!(e instanceof UploadNetworkError)) {
          throw e;
        }
      }
    }
  };

  for (;;) {
    if (signal?.aborted) {
      throw new UploadAbortedError();
    }
    if (offset >= total) {
      progress(total);
      return;
    }
    const from = offset;
    try {
      const res = await transport.put({
        url,
        headers: from === 0 ? { 'Content-Type': contentType } : { 'Content-Type': contentType, 'Content-Range': contentRangeOf(from, total) },
        body: from === 0 ? file : file.slice(from),
        onProgress: loaded => progress(from + loaded),
        signal
      });
      if (isTransientStatus(res.status)) {
        // «not now»: the same road as a lost connection (ask how much arrived, go on from there)
        throw new UploadNetworkError();
      }
      // a request of the data got an answer: the trouble before it is past
      calm();
      if (DONE.includes(res.status)) {
        progress(total);
        return;
      }
      if (res.status === RESUME) {
        // the cloud took a part of it and wants the rest; if that is no further than where this request began, three times in a row, it is not going anywhere
        offset = nextOffsetFromRange(res.header('Range'));
        stalls = offset > from ? 0 : stalls + 1;
        if (stalls >= MAX_STALLS) {
          throw new UploadHttpError(res.status);
        }
        progress(offset);
        continue;
      }
      throw new UploadHttpError(res.status);
    } catch (e) {
      if (!(e instanceof UploadNetworkError)) {
        throw e;
      }
      offset = await recover();
      if (offset > from) {
        // the cloud has more than before the failure: the file is moving, the spell is over
        calm();
      }
      if (offset >= total) {
        progress(total);
        return;
      }
      progress(offset);
    }
  }
}
